import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  screenLgMax,
  screenMdMin,
  screenSmMax,
  screenXlMin,
  screenXsMax,
  spacer40,
  spacer64,
} from '@spotify-internal/encore-foundation';
import { Banner } from '@spotify-internal/encore-creator-web';
import Card from '../Card';
import { Screen } from '../../../../shared/lib/useViewport';

const CardArea = styled.div`
  grid-area: cards;
  min-width: 0;

  @media (max-width: ${screenXsMax}) {
    margin-top: ${spacer40};
  }
`;

const CardGrid = styled(CardArea)`
  display: grid;

  @media (max-width: ${screenSmMax}) {
    grid-template-columns: 1fr;
    margin-bottom: ${spacer64};
  }

  @media (min-width: ${screenMdMin}) and (max-width: ${screenLgMax}) {
    grid-column-gap: ${spacer40};
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${screenXlMin}) {
    grid-column-gap: ${spacer40};
    grid-template-columns: repeat(3, 1fr);
  }
`;

// needs to be a div for creation of grid columns
const CardGridCol = styled.div``;

/* get the number of columns for card grid based on viewport */
export const getNumberOfColumns = viewport => {
  if (viewport < Screen.MD) {
    return 1;
  }
  if (viewport >= Screen.MD && viewport < Screen.XL) {
    return 2;
  }
  return 3;
};

/* filter out cards from specific column */
const columnFilter = (cardsList, numColumns, currentColIndex) =>
  cardsList.filter((value, index) => index % numColumns === currentColIndex);

export const sortCardsForColumns = (cardsList, numColumns) => {
  if (numColumns === 2) {
    return [columnFilter(cardsList, 2, 0), columnFilter(cardsList, 2, 1)];
  }

  if (numColumns === 3) {
    return [
      columnFilter(cardsList, 3, 0),
      columnFilter(cardsList, 3, 1),
      columnFilter(cardsList, 3, 2),
    ];
  }
  return [cardsList];
};

const getCardIndex = (numCols, curColIndex, curRowIndex) =>
  numCols * curRowIndex + curColIndex;

const CardGridLayout = React.memo(props => {
  const { artist, history, viewport, tracker, cardData } = props;

  const CardProps = {
    artist,
    history,
    viewport,
  };

  const totalCardNum = cardData.length;

  if (totalCardNum === 0) {
    return (
      <CardArea>
        <Banner variant={Banner.informational} contextual>
          Seems like something’s up. We’re having trouble loading your cards.
        </Banner>
      </CardArea>
    );
  }

  const numColumns = getNumberOfColumns(viewport);

  const filteredCards = cardData.filter(card => card.cardType === 'DISPLAY');
  const resortedCardsList = sortCardsForColumns(filteredCards, numColumns);

  return (
    <CardGrid data-testid="cards">
      {resortedCardsList.map((cardColumnList, colIndex) => (
        <CardGridCol key={colIndex}>
          {cardColumnList.map((card, rowIndex) => (
            <Card
              {...CardProps}
              key={card.uuid}
              cardNum={getCardIndex(numColumns, colIndex, rowIndex)}
              totalCardNum={totalCardNum}
              resourceUri={card.resourceUri}
              cardSource={card.cardSource}
              uuid={card.uuid}
              notificationClass={card.notificationClass}
              displayCard={card.displayCard}
              tracker={tracker}
            />
          ))}
        </CardGridCol>
      ))}
    </CardGrid>
  );
});

CardGridLayout.propTypes = {
  artist: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }).isRequired,
  viewport: PropTypes.number.isRequired,
  history: PropTypes.shape({}).isRequired,
  cardData: PropTypes.array.isRequired,
  tracker: PropTypes.shape({
    makeHomeCardEvent: PropTypes.func,
    trackImpression: PropTypes.func,
    trackInteraction: PropTypes.func,
  }),
};

/* eslint-disable-next-line import/no-default-export */
export default CardGridLayout;
